// @flow
import React from 'react';
import injectSheet from 'react-jss';
import classNames from 'classnames';
import SocialIcon from 'shared/components/SocialIcon';
import getResponsiveImageUrl from 'shared/lib/getResponsiveImageUrl';
import { formatDate, formatDateCN } from 'shared/lib/dateUtils';
import { getWebDomain } from 'shared/lib/clientSettings';
import Icon from '@ff/ui/Icon';
import {
  FONT_FAMILY_ROBOTO
} from 'shared/constants';

type HeroImageUrlProps =
  | {
      mobile: string,
      tablet: string,
      dekstop: string,
    }
  | string;

type Post = {
  region: string,
  title: string,
  published_at: Date,
  description: string,
  url: string,
  tags: Array<Object>,
  press_kit: string,
  social_share: Array<Object>,
  background_position: string,
  background_scale: string,
  heroImageUrl: HeroImageUrlProps,
  responsive_image: ?boolean
}

type Props = {
  classes: Object,
  pressRoom: boolean,
  post: Post
}

type State = {
  expanded: boolean,
  showImage: boolean
}

class PostCard extends React.Component<Props, State> {
  props: Props = this.props;

  node = React.createRef();

  observer;

  state = {
    expanded: false,
    showImage: false
  };

  componentDidMount() {
    this.imageObserver();
  }

  componentWillUnmount() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  imageObserver = () => {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0
    };

    if (typeof (IntersectionObserver) !== 'undefined') {
      this.observer = new IntersectionObserver(this.callback, options);
      this.observer.observe(this.node.current);
    } else {
      this.setShowImage();
    }
  }

  callback = (entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        this.setShowImage();
        this.observer.unobserve(entry.target);
      }
    });
  }

  setShowImage = () => {
    this.setState({ showImage: true });
  }

  render() {
    const { classes: c, post } = this.props;
    const { expanded, showImage } = this.state;
    const sectionString = post.section === 'FUTURESIGHT' ? 'futuresight' : 'press-room';
    const iconStyle = {
      fontSize: '20px',
      color: 'black',
      marginLeft: '8px',
      marginRight: '8px',
      textDecoration: 'none',
      cursor: 'pointer'
    };
    const domain = getWebDomain(post.region);

    return (
      <div className={c.cardArticle} ref={this.node}>
        <a
          className={c.imageLink}
          href={post.url}
        >
          <div className={classNames(c.cardHeroImg, showImage && c.responsiveImage)} />
        </a>
        <div className={c.cardContent}>
          <div className={c.cardDate}>
            {
              post.region === 'CN' ?
                formatDateCN(post.published_at) :
                formatDate(post.published_at)
            }
          </div>
          <div className={c.cardTitle}>
            <a href={post.url}>{post.title}</a>
          </div>
          <div className={c.cardDesc}>{post.description}</div>
          <div>
            <a href={post.url} className={c.cardReadMore}>
              {post.region === 'US' ? 'read more' : '阅读更多'}
            </a>
            {post.press_kit &&
              <a href={post.press_kit} className={c.cardReadMore}>
                {post.region === 'US' ? 'press kit' : '新闻资料'}
              </a>
            }
          </div>
          {post.tags.length > 0 &&
            <div className={c.cardTags}>
              <span>
                {post.region === 'US' ? 'Tags: ' : '标签：'}
              </span>
              {post.tags.map(tag => (
                <a
                  href={`/${post.region.toLowerCase()}/${sectionString}/tags/${tag.tag.toLowerCase()}`}
                  key={tag.tag}
                >
                  {tag.tag}
                </a>
              ))}
            </div>
          }
          <div className={c.cardSocialShare}>
            <Icon
              name="ff-icon-share"
              className={c.socialIcon}
              onMouseEnter={() => this.setState({ expanded: true })}
              onMouseLeave={() => this.setState({ expanded: false })}
            />
            <div
              className={classNames(c.iconList, expanded && c.expanded)}
              onMouseEnter={() => this.setState({ expanded: true })}
              onMouseLeave={() => this.setState({ expanded: false })}
            >
              {post.social_share.map((link, index) => (
                <SocialIcon
                  key={index}
                  iconStyle={iconStyle}
                  link={link}
                  generateQRCode={`${domain}${post.url}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

// Update image URL based on region
const regionalImage = (props) => {
  const { post } = props;

  const heroImageUrl = post.region === 'US' ?
    post.heroImageUrl :
    post.heroImageUrl.replace('genesis-cdn.ff', 'genesis-cdn.faradayfuturecn');

  return heroImageUrl;
};

// Update image URL based on region and generate responsive URL
const responsiveRegionalImage = (props: Props, size) => {
  const { post: { responsive_image: isResponsive } } = props;

  const heroImageUrl = regionalImage(props);

  if (!isResponsive) {
    return `url(${heroImageUrl})`;
  }

  return getResponsiveImageUrl(heroImageUrl, size);
};

// Use a separate function for images using the cropper tool
const croppedRegionalImage = (props) => {
  const { post } = props;
  // Use the desktop image
  return post.region === 'US' ?
    post.heroImageUrl.desktop :
    post.heroImageUrl.desktop.replace('genesis-cdn.ff', 'genesis-cdn.faradayfuturecn');
};

const croppedImage = (props: Props) => {
  const heroImageUrl = croppedRegionalImage(props);
  return `url(${heroImageUrl})`;
};

const styles = {
  cardArticle: {
    marginTop: '36px',
    marginBottom: '36px',
    paddingBottom: '36px',
    width: '33.3%',
    // 4 width in Press Room, 3 in Futuresight
    // Wrapped in media query to avoid overriding other settings
    '@media(min-width: 1281px)': {
      width: ({ pressRoom }) => pressRoom ? '25%' : '33.3%',
    },
    paddingLeft: '18px',
    paddingRight: '18px',
    fontFamily: FONT_FAMILY_ROBOTO,
    '@media(max-width: 960px)': {
      width: '50%'
    },
    '@media(max-width: 768px)': {
      width: '100%',
      paddingLeft: '0',
      paddingRight: '0',
      paddingBottom: '0',
      marginBottom: '0'
    }
  },
  cardHeroImg: {
    width: '100%',
    height: '20vh',
    maxHeight: '194px',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    backgroundPosition: 'center center'
  },
  responsiveImage: {
    backgroundImage: props => props?.post?.heroImageUrl?.desktop ?
      croppedImage(props) : responsiveRegionalImage(props, '800w'),
  },
  imageLink: {
    height: '100%',
    width: '100%'
  },
  cardContent: {
    padding: '18px 36px',
    backgroundColor: 'white',
    borderTop: '1px solid #EAEAEA',
    '@media(max-width: 768px)': {
      paddingLeft: '18px',
      paddingRight: '18px'
    }
  },
  cardDate: {
    fontSize: '12px',
    fontWeight: '600',
    letterSpacing: '1.2px',
    lineHeight: '1.75em',
    padding: '18px 0',
    textTransform: 'uppercase'
  },
  cardTitle: {
    fontSize: '23px',
    fontWeight: '300',
    letterSpacing: '.025em',
    lineHeight: '1.5em',
    paddingBottom: '18px',
    cursor: 'pointer',
    '& > a': {
      color: 'black',
      textDecoration: 'none'
    },
  },
  cardSocialShare: {
    padding: '18px 0',
    display: 'flex',
    alignItems: 'center'
  },
  iconList: {
    width: '0',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    transition: 'all .4s ease'
  },
  expanded: {
    width: '100%',
    overflow: 'visible',
    animation: '.4s $removeHiddenOverflow'
  },
  '@keyframes removeHiddenOverflow': {
    from: {
      overflow: 'hidden'
    }
  },
  socialIcon: {
    paddingRight: '10px',
    fontSize: '15px',
    linegHeight: '1.2em',
    cursor: 'pointer'
  },
  cardDesc: {
    paddingBottom: '36px',
    fontSize: '15px',
    lineHeight: '1.75em'
  },
  cardReadMore: {
    paddingBottom: '18px',
    fontSize: '12px',
    textDecoration: 'none',
    fontWeight: '600',
    marginRight: '25px',
    letterSpacing: '.1em',
    lineHeight: '1.75em',
    textTransform: 'uppercase',
    cursor: 'pointer',
    color: 'black',
    '&:hover': {
      color: 'hsl(16, 34%, 65%)'
    }
  },
  cardTags: {
    padding: '18px 0',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    fontFamily: FONT_FAMILY_ROBOTO,
    fontSize: '12px',
    fontWeight: '600',
    lineHeight: '1.75em',
    borderBottom: '1px solid #eaeaea',
    '& > a': {
      textDecoration: 'none',
      color: 'black',
      textTransform: 'capitalize',
      fontSize: '12px',
      fontWeight: '600',
      lineHeight: '1.75em',
      marginRight: '5px',
      transition: 'all .2s ease',
      '&:after': {
        content: '","',
        display: 'inline-block'
      },
      '&:last-child:after': {
        content: '""'
      }
    }
  }
};

export default injectSheet(styles)(PostCard);
